import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { UserX } from 'lucide-react';
import api from '../../services/api';
import Avatar from '../../components/ui/Avatar';
import { splitFullName } from '../../components/ui/PersonCell';

// ═══ ORTAK POZİSYON SEÇİCİ (2026-09-30) ═════════════════════════════════════
//
// Uygulamadaki HER pozisyon seçicisi (kadro talebi mevcut/üst pozisyon, Pozisyonlar
// sayfası üst pozisyon, onay akışı adımı, işe alıştırma / işe hazırlık şablonları,
// hedef ve geri bildirim şablonları, yan hak paketi) buradan beslenir.
//
// Neden: seçiciler "[POS-0012] Yazılım Müdürü" ya da "POS-0012 - BT - Yazılım Müdürü"
// gösteriyordu ve yalnız KODDA arıyordu. Müşteri doğru üst pozisyonu bulamıyordu —
// insanlar pozisyonu koduyla değil, ÜNVANI, DEPARTMANI ve orada OTURAN KİŞİYLE tanır
// ("Ahmet Bey'e bağlı çalışacak"). Workday / SuccessFactors pozisyon seçicileri de bu
// yüzden ünvan + birim + oturan kişi (ya da "Boş") gösterir.
//
// Sunucu sözleşmesi (backend Dtos/Position/PositionResponse.cs): titleName,
// departmentName, code, isVacant, occupantName, occupantPhoto. Arama `q[like]`:
// sözcükler arası VE, kod / ünvan / departman / oturan kişi arası VEYA
// (PositionDataAccess.GetAll).

// Tek satırlık metin — seçili değerde ve düz metin gereken yerlerde (tablo hücresi).
// Sıra: ünvan → departman → oturan kişi → kod (en ayırt edici bilgi en sonda).
export const positionOptionLabel = (p) => {
  if (!p) return '';
  const parts = [p.titleName, p.departmentName, p.occupantName, p.code].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : (p.id ? String(p.id) : '');
};

// Seçenek nesnesi. `profilePicture` anahtarı BİLEREK YOK — AsyncGenericSelect o anahtarı
// taşıyan seçeneği "kişi" sayar ve seçili değeri avatarlı kişi hücresi olarak çizer
// (components/ui/personOption.js); pozisyon bir kişi değildir. Fotoğraf `occupantPhoto`da.
export const toPositionOption = (p) => ({
  id: p.id,
  name: positionOptionLabel(p),
  titleName: p.titleName || null,
  departmentName: p.departmentName || null,
  code: p.code || null,
  occupantName: p.occupantName || null,
  occupantPhoto: p.occupantPhoto || null,
  isVacant: p.isVacant === true,
});

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const VacantIcon = styled.div`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1.5px dashed ${({ theme }) => theme.colors.text.light.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Lines = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Primary = styled.span`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Secondary = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Boş pozisyon vurgusu: metin rengi ikincil satırla aynı, kalınlık ayırır — sarı uyarı
// tonu (#FFC107) beyaz zeminde okunmaz.
const VacantText = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

// Açılır listedeki iki satırlı seçenek: ünvan / "departman · kişi (ya da Boş) · kod".
// Kişi varsa fotoğrafı (yoksa baş harfleri), boş pozisyonda kesikli çerçeveli simge.
export function PositionOption({ option }) {
  const { t } = useTranslation();
  if (!option) return null;
  const primary = option.titleName || option.code || option.name;
  const occupant = option.occupantName
    ? option.occupantName
    : (option.isVacant ? <VacantText>{t('position.vacant')}</VacantText> : null);
  const secondaryParts = [
    option.departmentName,
    occupant,
    option.titleName ? option.code : null,
  ].filter(Boolean);
  const { firstName, lastName } = splitFullName(option.occupantName || '');

  return (
    <Row data-role="position-option">
      {option.occupantName ? (
        <Avatar src={option.occupantPhoto || undefined} firstName={firstName} lastName={lastName} variant="circle" size={28} />
      ) : (
        <VacantIcon aria-hidden="true"><UserX size={14} /></VacantIcon>
      )}
      <Lines>
        <Primary>{primary}</Primary>
        {secondaryParts.length > 0 && (
          <Secondary>
            {secondaryParts.map((part, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={i}>{i > 0 ? ' · ' : ''}{part}</React.Fragment>
            ))}
          </Secondary>
        )}
      </Lines>
    </Row>
  );
}

export const renderPositionOption = (opt) => <PositionOption option={opt} />;

// GET istekleri interceptor'da reddedilmez (sinyal res.data.status) — veri yoksa boş liste.
export function buildPositionAsync() {
  return {
    onSearch: async (q) => {
      const params = q ? { 'q[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/positions', { params });
      const r = res.data?.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      return items.map(toPositionOption);
    },
    onGetById: async (id) => {
      if (!id) return null;
      const res = await api.get(`/v1/positions/${id}`);
      const p = res.data?.data;
      return p ? toPositionOption(p) : null;
    },
    renderOption: renderPositionOption,
  };
}

export const positionAsync = buildPositionAsync();
